import {
	type ImageFieldsFragment,
	type ThreeSixtyImageFieldsFragment,
	type VideoFieldsFragment
} from '../../__generated__/graphql-client-types';
import { defaultCloudinaryOptions } from '../../constants/images';
import { type CloudinaryOptions, generateCloudinaryUrl } from '../cloudinary-helper/cloudinary-helper';

export type Media = ImageFieldsFragment | VideoFieldsFragment | ThreeSixtyImageFieldsFragment;

type MediaCondition = {
	maxWidth: number;
	unit?: 'px' | 'em';
};

/** Defines a breakpoint-specific image configuration used for resolution switching (srcSet/sizes) and art direction (<source> elements). */
export type Variation = {
	mediaCondition: MediaCondition;
	options: CloudinaryOptions;
	/** Only used for art direction — allows swapping to a different image per breakpoint. Ignored for resolution switching. */
	publicID?: string;
};

export const isVideo = (item: Media): item is VideoFieldsFragment => 'hashKey' in item;

export const isThreeSixtyImage = (item: Media): item is ThreeSixtyImageFieldsFragment => item.__typename === 'ThreeSixtyImage';

export const isImage = (item: Media): item is ImageFieldsFragment => item.__typename === 'Image';

export const generateDimensionAttributes = (width: number, height: number): Partial<Pick<HTMLImageElement, 'width' | 'height'>> => {
	return {
		width: width > 0 ? width : undefined,
		height: height > 0 ? height : undefined
	};
};

export const fallbackImage = 'data:image/gif;base64,R0lGODlhAQABAAD/ACwAAAAAAQABAAACADs=';

export type SrcSetData =
	| {
			srcSet: string;
			sizes: string;
	  }
	| {
			srcSet: undefined;
			sizes: undefined;
	  };

export const generateSrcSetData = (publicId: string, primaryOptions: CloudinaryOptions, variations: Variation[]): SrcSetData => {
	if (variations.length) {
		// Encode the publicID to handle special characters that can break srcset
		const encodedPublicId = encodeURIComponent(publicId);
		const sources = variations.map((variation) => {
			const source = generateCloudinaryUrl(encodedPublicId, variation.options);
			return `${source} ${variation.options.width}w`;
		});
		const sizes = variations.map((variation) => {
			const { options, mediaCondition } = variation;
			const { maxWidth, unit } = mediaCondition;
			return `(max-width: ${maxWidth}${unit || 'px'}) ${options.width}px`;
		});

		// need to add the default srcSet and size
		sizes.push(`${primaryOptions.width}px`);
		const primarySource = generateCloudinaryUrl(encodedPublicId, primaryOptions);
		sources.push(`${primarySource} ${primaryOptions.width}w`);

		return {
			srcSet: sources.join(', '),
			sizes: sizes.join(', ')
		};
	} else {
		return {
			srcSet: undefined,
			sizes: undefined
		};
	}
};

type PixelRatios = number[];

export function generateSrcSetDataFromDPRs(publicID: string, pixelRatios: PixelRatios, options = defaultCloudinaryOptions): SrcSetData {
	if (options.width === undefined || options.height === undefined) {
		throw new Error('Width and Height must be defined in Cloudinary options to generate DPR srcSet data');
	}

	const { width, height } = options;

	const sources = pixelRatios.reduce((sourceStrings: string[], pixelRatio) => {
		const newWidth = width * pixelRatio;
		const newHeight = height * pixelRatio;
		const source = generateCloudinaryUrl(publicID, { ...options, width: newWidth, height: newHeight });
		sourceStrings.push(`${source} ${pixelRatio}x`);
		return sourceStrings;
	}, []);

	return {
		srcSet: sources.join(','),
		sizes: ''
	};
}

export const waitForImagesToLoad = async () => {
	const images = Array.from(document.images);
	const promises = images.map((img) => {
		if (img.complete) {
			return Promise.resolve();
		}
		return new Promise<void>((resolve) => {
			img.onload = () => resolve();
			img.onerror = () => resolve();
		});
	});

	await Promise.all(promises);
};
